import React from 'react';
import teamImage from '../assets/fabrice-villard-Jrl_UQcZqOc-unsplash.jpg';

const AboutPage: React.FC = () => {
  const team = [
    {
      name: 'Sarah Chen',
      role: 'Founder & Creative Director',
      bio: 'With over 8 years in web design and digital strategy, Sarah brings a unique vision for creating serene digital experiences.',
      expertise: ['UI/UX Design', 'Digital Strategy', 'Brand Development'],
      image: '/api/placeholder/150/150'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Lead Developer',
      bio: 'Full-stack developer passionate about clean code and performance optimization. Marcus ensures every website runs smoothly.',
      expertise: ['React/Node.js', 'Performance Optimization', 'API Development'],
      image: '/api/placeholder/150/150'
    },
    {
      name: 'Emily Johnson',
      role: 'SEO & Marketing Specialist',
      bio: 'Digital marketing expert who helps businesses grow their online presence through strategic SEO and content marketing.',
      expertise: ['SEO Optimization', 'Content Strategy', 'Analytics'],
      image: '/api/placeholder/150/150'
    }
  ];

  const values = [
    {
      title: 'Serenity in Design',
      description: 'We believe that great design should feel effortless and peaceful, creating calm user experiences that encourage engagement.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )
    },
    {
      title: 'Quality Craftsmanship',
      description: 'Every line of code, every pixel, every interaction is carefully crafted to deliver exceptional quality and performance.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Client Success',
      description: 'Your success is our success. We measure our work by the growth and satisfaction of our clients.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: 'Innovation & Growth',
      description: 'We stay ahead of digital trends to ensure your website uses the latest technologies and best practices.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  const stats = [
    { number: '100+', label: 'Websites Delivered' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24h', label: 'Average Response Time' },
    { number: '5+', label: 'Years Experience' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-serenity-blue to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              About Serenity Webcrafts
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-blue-100">
              We're passionate about creating digital experiences that bring serenity to your business 
              and joy to your customers.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-serenity-dark mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                At Serenity Webcrafts, we believe that your website should be more than just a digital brochure. 
                It should be a powerful tool that grows your business while providing a serene, enjoyable 
                experience for your visitors.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Founded in 2019, we've helped over 100 businesses transform their digital presence with 
                websites that are not only beautiful but also functional, fast, and results-driven.
              </p>
              <p className="text-lg text-gray-600">
                Our approach combines cutting-edge technology with timeless design principles to create 
                digital experiences that stand the test of time.
              </p>
            </div>
            <div className="relative">
              <img
                src={teamImage}
                alt="Our workspace"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-serenity-blue/20 to-transparent rounded-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-serenity-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-serenity-dark mb-4">
              Our Track Record
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that reflect our commitment to excellence
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-serenity-blue mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-serenity-dark mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core principles guide everything we do and ensure we deliver 
              exceptional results for every client.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-serenity-blue rounded-full flex items-center justify-center text-white mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-serenity-dark mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-serenity-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-serenity-dark mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Passionate professionals dedicated to bringing your digital vision to life
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="aspect-w-1 aspect-h-1 w-full h-64 bg-gray-200 flex items-center justify-center">
                  <div className="w-32 h-32 bg-serenity-blue rounded-full flex items-center justify-center text-white text-4xl font-bold">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-serenity-dark mb-2">
                    {member.name}
                  </h3>
                  <p className="text-serenity-blue font-medium mb-4">
                    {member.role}
                  </p>
                  <p className="text-gray-600 mb-4">
                    {member.bio}
                  </p>
                  <div>
                    <h4 className="font-semibold text-serenity-dark mb-2">Expertise:</h4>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="px-3 py-1 bg-serenity-blue/10 text-serenity-blue rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-serenity-dark mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined approach that ensures your project is delivered on time, 
              on budget, and beyond expectations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We learn about your business, goals, and target audience to create a strategic foundation.'
              },
              {
                step: '02',
                title: 'Design',
                description: 'Our team creates beautiful, user-friendly designs that reflect your brand and engage your audience.'
              },
              {
                step: '03',
                title: 'Development',
                description: 'We bring the designs to life with clean, efficient code that ensures optimal performance.'
              },
              {
                step: '04',
                title: 'Launch & Support',
                description: 'We launch your website and provide ongoing support to ensure continued success.'
              }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-serenity-blue rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-6">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-serenity-dark mb-4">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-serenity-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Let's create something beautiful together. We're excited to learn about your project 
            and help bring your digital vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-serenity-blue px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-300"
            >
              Start a Conversation
            </a>
            <a
              href="/packages"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-serenity-blue transition-colors duration-300"
            >
              View Our Packages
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
